// Rung 5 — your own infrastructure.
//
// A capability says what a run *is*. It does not say where to find one,
// and §5.4 is emphatic about that: a link is a key, not a location. Which
// leaves a hole — a fresh phone with a perfect capability and nobody to
// dial — and a seed is the machine that closes it.
//
// The thing to keep straight is that a seed address is the opposite kind
// of object from everything else this console mints. A capability must be
// secret, must expire, must be narrow. An address is public, long-lived,
// and grants nothing: a peer answering on it can still read no thread it
// does not hold the key for. So it travels differently, and this screen
// is mostly about being honest about what that costs.

import { useCallback, useState } from "react";
import { Note, Panel } from "./Bits";
import type { DispatchBundle } from "../ops/engine";
import type { FleetState, StoredSeed } from "../ops/state";
import { nowSeconds } from "../config";

export function Seed({
  state,
  threads,
  onSave,
  onForget
}: {
  state: FleetState;
  threads: DispatchBundle | null;
  onSave(seed: StoredSeed): void;
  onForget(): void;
}): JSX.Element {
  const [text, setText] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  const read = useCallback((value: string) => {
    setText(value);
    setProblem(null);
    const trimmed = value.trim();
    if (!trimmed || !threads) return;
    try {
      const card = threads.parseSeedCardUrl(trimmed);
      const undialable = card.addresses.filter(address => !threads.isDialableAddress(address));
      if (undialable.length === card.addresses.length) {
        setProblem(
          "Every address on that card is one nothing can reach — a loopback or a 0.0.0.0, which is "
          + "what the machine was told to listen on rather than where it is. It would scan cleanly "
          + "in the depot and connect to nothing."
        );
        return;
      }
      onSave({
        label: card.label || "Our seed",
        addresses: card.addresses.filter(address => threads.isDialableAddress(address)),
        enrolledUnixSeconds: nowSeconds(),
        hintCustomerLinks: state.seed?.hintCustomerLinks ?? false
      });
      setText("");
    } catch {
      setProblem("That is not a seed card. It should start with wayfind://seed#.");
    }
  }, [threads, onSave, state.seed?.hintCustomerLinks]);

  if (state.seed) {
    return (
      <Panel
        eyebrow="Your own seed"
        aside={
          <button type="button" className="button button--small button--danger" onClick={onForget}>
            Forget it
          </button>
        }
      >
        <p className="subtitle">{state.seed.label}</p>
        <ul className="rows" style={{ listStyle: "none", padding: 0, margin: "0.5rem 0 0" }}>
          {state.seed.addresses.map(address => (
            <li key={address} className="rowcard">
              <code className="mono wf-code" style={{ wordBreak: "break-all" }}>{address}</code>
            </li>
          ))}
        </ul>

        <hr className="divider" />

        <Note title="It rides inside the sealed job, and nowhere else by default." tone="quiet">
          Every job this console seals from now on carries these addresses to the phone it was
          sealed to, and stops there. They are not in a job offer and never will be: an offer goes
          to couriers nobody has met, and for a two-van operation whose seed is the machine in the
          back office, that is a home address with an open port on it.
        </Note>

        <label className="checkline" style={{ marginTop: "0.7rem" }}>
          <input
            type="checkbox"
            checked={state.seed.hintCustomerLinks}
            onChange={event => onSave({ ...state.seed!, hintCustomerLinks: event.target.checked })}
          />
          <span>
            Put the address in customers’ links too.
            <span className="field__hint" style={{ display: "block" }}>
              It goes in the part of the address that <em>is</em> transmitted — which is right for a
              public address and wrong for a key, and is why the key stays where it is. Turning this
              on discloses the address to whoever hosts the page, whoever logs the URL, and whoever
              the customer forwards it to. Worth it for an address you chose to publish; not for the
              machine under your desk.
            </span>
          </span>
        </label>

        <hr className="divider" />
        <div className="kv">
          <span className="kv__key">What it costs</span>
          <p className="kv__value">
            A single point of failure for <strong>joining</strong>, and for nothing else. A mesh that
            has formed keeps working when the seed goes down; what breaks is a cold phone on a day
            it is off.
          </p>
          <span className="kv__key">What it sees</span>
          <p className="kv__value">
            Which peers connect, when, and from which addresses. Not what any run is doing — records
            are sealed to a key it does not hold — and not which threads exist, because the topics
            are pseudorandom and rotate.
          </p>
          <span className="kv__key">The catch</span>
          <p className="kv__value">
            A machine behind a home router is not dialable. It needs a forwarded port or a relay in
            front of it, and a small rented box sidesteps the question entirely.
          </p>
        </div>
      </Panel>
    );
  }

  return (
    <Panel eyebrow="Your own seed">
      <p className="subtitle">
        Enough phones that the mesh is worth something. A seed is one machine your drivers can all
        dial — it is how a phone that has never met anything finds the others at all. Run the keeper
        with <code className="mono wf-code">--seed-card</code> and it prints one.
      </p>
      <div className="field" style={{ marginTop: "0.7rem" }}>
        <label className="field__label" htmlFor="seed-card">The card the keeper printed</label>
        <textarea
          id="seed-card"
          className="textarea"
          placeholder="wayfind://seed#…"
          value={text}
          onChange={event => read(event.target.value)}
        />
      </div>
      {problem ? (
        <div className="banner banner--halt" role="alert" style={{ marginTop: "0.6rem" }}>
          <span className="banner__mark">No</span>
          <p className="banner__text">{problem}</p>
        </div>
      ) : null}
      <Note title="A card carries no signature, and that is deliberate." tone="quiet">
        There is no authority to claim here. The peer id on the end of the address is what
        authenticates the machine when a phone connects, so a card naming the wrong one fails to
        connect rather than impersonating anybody. Signing it would only invite somebody to read
        “signed by the dispatcher” as “safe to trust”.
      </Note>
    </Panel>
  );
}
